import React from 'react';
import PropTypes from 'prop-types';
import {Link} from 'react-router-dom';
import { resolveStoryblokLink } from '../../../utils/StoryblokFunctions';

import '../../../css/storyblok-custom.css';
import '../../../css/tile.css';


/**
 * A card for a single CEO/founder appearing in a session.
 */
const CeoSessionCard = ({
    name,
    title,
    companyName,
    sessionTitle,
    sessionSummary,
    photo,
    logo,
    link,
    buttonText,
    topics,
    startupStages,
    featured,
}) => {

    const photoFilename = (photo && photo.filename) ? photo.filename : "";
    const logoFilename = (logo && logo.filename) ? logo.filename : "";
    const {route, isExternal} = resolveStoryblokLink(link);
    const tags = [].concat(topics ? topics : [], startupStages ? startupStages : [])
        .filter(tag => typeof tag === 'string' && tag !== "");

    const cardContent = (
        <>
            {
                photoFilename ?
                    <div className="ceo-session-card-photo">
                        <img src={photoFilename} alt={name ? name : companyName}/>
                    </div>
                    : null
            }
            <div className="ceo-session-card-body">
                {name ? <h4 className="ceo-session-card-name">{name}</h4> : null}
                {title ? <div className="ceo-session-card-title">{title}</div> : null}
                {companyName ? <div className="ceo-session-card-company">{companyName}</div> : null}
                {sessionTitle ? <div className="ceo-session-card-session">{sessionTitle}</div> : null}
                {sessionSummary ? <div className="ceo-session-card-summary"><small>{sessionSummary}</small></div> : null}
                {
                    tags.length > 0 ?
                        <div className="ceo-session-card-tags">
                            {tags.map((tag, index) => <span className="ceo-session-card-tag" key={index}>{tag}</span>)}
                        </div>
                        : null
                }
            </div>
            {
                logoFilename ?
                    <div className="ceo-session-card-logo">
                        <img src={logoFilename} alt={companyName ? companyName : name}/>
                    </div>
                    : null
            }
        </>
    );

    return (
        <div className={"panel ceo-session-card" + (featured ? " featured" : "")}>
            {cardContent}
            {
                route ?
                    <div className="ceo-session-card-link">
                        {
                            isExternal ?
                                <a href={route} title={buttonText ? buttonText : name}
                                    target="_blank" rel="noopener noreferrer"
                                    className="btn btn-primary btn-image-text nowrap">
                                    {buttonText ? buttonText : "View Session"}
                                </a>
                                :
                                <Link to={route} title={buttonText ? buttonText : name}
                                    className="btn btn-primary btn-image-text nowrap">
                                    {buttonText ? buttonText : "View Session"}
                                </Link>
                        }
                    </div>
                    : null
            }
        </div>
    );
};


CeoSessionCard.propTypes = {
    /**
     * Name of the person
     */
    name: PropTypes.string,
    /**
     * Their role/title
     */
    title: PropTypes.string,
    /**
     * Company they represent
     */
    companyName: PropTypes.string,
    /**
     * Title of the session they present
     */
    sessionTitle: PropTypes.string,
    /**
     * Summary of the session
     */
    sessionSummary: PropTypes.string,
    /**
     * Storyblok asset for the person's photo
     */
    photo: PropTypes.any,
    /**
     * Storyblok asset for the company logo
     */
    logo: PropTypes.any,
    /**
     * Storyblok multilink to the session
     */
    link: PropTypes.any,
    /**
     * Label for the link. Defaults to 'View Session'.
     */
    buttonText: PropTypes.string,
    /**
     * Topic tags
     */
    topics: PropTypes.array,
    /**
     * Startup stage tags
     */
    startupStages: PropTypes.array,
    /**
     * Is this card highlighted?
     */
    featured: PropTypes.bool,
};

CeoSessionCard.defaultProps = {
    name: "",
    title: "",
    companyName: "",
    sessionTitle: "",
    sessionSummary: "",
    photo: null,
    logo: null,
    link: null,
    buttonText: "",
    topics: [],
    startupStages: [],
    featured: false,
};

export default CeoSessionCard;
